import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import { stageMeta, stageLabel } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Printer, QrCode, Trash2, Scale, Boxes, ChevronRight, GripVertical, Clock, Pencil, Search, AlertTriangle, Tags, Droplets } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

function fmtDuration(iso, nowMs, t) {
  if (!iso) return null;
  const start = new Date(iso).getTime();
  if (isNaN(start)) return null;
  let mins = Math.max(0, Math.floor((nowMs - start) / 60000));
  const d = Math.floor(mins / 1440); mins -= d * 1440;
  const h = Math.floor(mins / 60); const m = mins - h * 60;
  const parts = [];
  if (d) parts.push(`${d}d`);
  if (h) parts.push(`${h}h`);
  parts.push(`${m}m`);
  return `${parts.join(" ")} ${t("in_stage")}`;
}

// green -> amber -> red heat based on fraction of the alert threshold
function heatFor(startIso, nowMs, alertHours, isLast) {
  if (isLast || !startIso) return { color: "#6b7280", overdue: false };
  const ratio = (nowMs - new Date(startIso).getTime()) / (alertHours * 3600000);
  if (ratio >= 1) return { color: "#dc2626", overdue: true };
  if (ratio >= 0.5) return { color: "#d97706", overdue: false };
  return { color: "#16a34a", overdue: false };
}

export default function QueueManagement() {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [overStage, setOverStage] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [editOrder, setEditOrder] = useState(null);
  const [lookup, setLookup] = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const stages = user?.stages || [];
  const stageIds = stages.map((s) => s.id);
  const lastStage = stageIds[stageIds.length - 1];
  const alertHours = user?.stage_alert_hours ?? 24;

  const load = () => api.get("/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => { const i = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(i); }, []);

  const doLookup = async () => {
    const n = String(lookup).trim();
    if (!/^\d+$/.test(n)) return;
    try {
      const { data } = await api.get(`/crates/lookup/${n}`);
      if (data.found) {
        const stage = stages.find((s) => s.id === data.status);
        setLookupResult({ ...data, number: n, stageLabel: stage ? stageLabel(t, stage) : data.status });
      } else {
        setLookupResult({ found: false, number: n });
      }
    } catch { setLookupResult({ found: false, number: n }); }
  };

  const toggleSelect = (id) => setSelectedIds((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAllWithCrates = () => setSelectedIds(new Set(orders.filter((o) => (o.crates || []).length > 0).map((o) => o.id)));
  const printSelected = () => {
    const ids = [...selectedIds];
    if (ids.length) window.open(`/crate-tags?ids=${ids.join(",")}`, "_blank");
  };

  const setStatus = async (o, status) => {
    if (o.status === status) return;
    const stamp = new Date().toISOString();
    setOrders((prev) => prev.map((x) => (x.id === o.id ? { ...x, status, stage_entered_at: stamp } : x)));
    const stage = stages.find((s) => s.id === status);
    try { await api.put(`/orders/${o.id}/status`, { status }); toast.success(stageLabel(t, stage || { id: status })); }
    catch { load(); }
  };

  const advance = (o) => {
    const idx = stageIds.indexOf(o.status);
    if (idx >= 0 && idx < stageIds.length - 1) setStatus(o, stageIds[idx + 1]);
  };

  const remove = async (id) => { await api.delete(`/orders/${id}`); load(); };

  const onDrop = (stage) => {
    const o = orders.find((x) => x.id === dragId);
    if (o) setStatus(o, stage);
    setDragId(null); setOverStage(null);
  };

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="queue-page">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{t("queue_title")}</h1>
          <p className="mt-1 text-muted-foreground">{t("queue_sub")}</p>
        </div>
        <p className="flex items-center gap-2 rounded-lg bg-olive-light px-3 py-2 text-xs text-olive">
          <GripVertical className="h-3.5 w-3.5" /> {t("drag_hint")}
        </p>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="crate-lookup-input" type="number" inputMode="numeric" placeholder={t("lookup_crate")}
              value={lookup} onChange={(e) => setLookup(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") doLookup(); }}
              className="h-9 w-56 pl-9" />
          </div>
          <Button data-testid="crate-lookup-btn" size="sm" variant="outline" onClick={doLookup} className="rounded-lg">{t("find")}</Button>
          {lookupResult && (
            lookupResult.found ? (
              <button data-testid="lookup-result" onClick={() => { setHighlightId(lookupResult.id); setLookupResult(lookupResult); }}
                className="flex items-center gap-2 rounded-lg bg-olive-light px-3 py-1.5 text-xs font-medium text-olive">
                #{lookupResult.number} → {lookupResult.client_name} · {lookupResult.order_code} · {lookupResult.stageLabel}
              </button>
            ) : (
              <span data-testid="lookup-result" className="rounded-lg bg-secondary px-3 py-1.5 text-xs text-muted-foreground">
                #{lookupResult.number} — {t("crate_not_active")}
              </span>
            )
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button data-testid="select-mode-btn" size="sm" variant={selectMode ? "default" : "outline"}
            onClick={() => { setSelectMode((v) => !v); setSelectedIds(new Set()); }}
            className={`rounded-lg ${selectMode ? "bg-primary hover:bg-olive-hover" : ""}`}>
            <Tags className="mr-1 h-4 w-4" /> {selectMode ? t("cancel") : t("select_batches")}
          </Button>
          {selectMode && (
            <>
              <Button data-testid="select-all-tags-btn" size="sm" variant="outline" onClick={selectAllWithCrates} className="rounded-lg">{t("select_all")}</Button>
              <Button data-testid="print-selected-tags-btn" size="sm" disabled={selectedIds.size === 0} onClick={printSelected} className="rounded-lg bg-primary hover:bg-olive-hover">
                <Printer className="mr-1 h-4 w-4" /> {t("print_tags")} ({selectedIds.size})
              </Button>
            </>
          )}
        </div>

        <div className="flex gap-4 overflow-x-auto pb-4 no-scrollbar">
          {stages.map((stage, si) => {
            const meta = stageMeta(stage.id, si);
            const Icon = meta.icon;
            const items = orders.filter((o) => o.status === stage.id);
            const overdueCount = stage.id === lastStage ? 0 : items.filter((o) => {
              const s = o.stage_entered_at || o.updated_at || o.created_at;
              return s && (now - new Date(s).getTime()) > alertHours * 3600000;
            }).length;
            const isOver = overStage === stage.id;
            return (
              <div
                key={stage.id}
                data-testid={`queue-col-${stage.id}`}
                onDragOver={(e) => { e.preventDefault(); setOverStage(stage.id); }}
                onDragLeave={() => setOverStage((s) => (s === stage.id ? null : s))}
                onDrop={() => onDrop(stage.id)}
                className={`flex w-[280px] shrink-0 flex-col rounded-xl transition-colors ${isOver ? "bg-olive-light/60 ring-2 ring-primary/40" : ""}`}
              >
                <div className="mb-3 flex items-center justify-between rounded-lg px-3 py-2" style={{ background: `${meta.color}14` }}>
                  <span className="flex items-center gap-2 text-sm font-semibold" style={{ color: meta.color }}>
                    <Icon className="h-4 w-4" /> {stageLabel(t, stage)}
                  </span>
                  <span className="flex items-center gap-1.5">
                    {overdueCount > 0 && (
                      <span data-testid={`overdue-count-${stage.id}`} className="flex items-center gap-0.5 rounded-full bg-red-600 px-1.5 py-0.5 text-[10px] font-bold text-white">
                        <AlertTriangle className="h-3 w-3" /> {overdueCount}
                      </span>
                    )}
                    <span className="rounded-full bg-background px-2 py-0.5 font-mono text-xs font-semibold">{items.length}</span>
                  </span>
                </div>
                <div className="flex min-h-[80px] flex-col gap-3 px-1">
                  {items.length === 0 && (
                    <div className="rounded-lg border border-dashed border-border py-8 text-center text-xs text-muted-foreground">{t("empty_stage")}</div>
                  )}
                  {items.map((o) => {
                    const startIso = o.stage_entered_at || o.updated_at || o.created_at;
                    const dur = fmtDuration(startIso, now, t);
                    const heat = heatFor(startIso, now, alertHours, stage.id === lastStage);
                    const overdue = heat.overdue;
                    const isHi = highlightId === o.id;
                    return (
                    <div
                      key={o.id}
                      data-testid={`queue-card-${o.order_code}`}
                      draggable
                      onDragStart={() => setDragId(o.id)}
                      onDragEnd={() => { setDragId(null); setOverStage(null); }}
                      className={`cursor-grab rounded-xl border bg-card p-4 shadow-sm transition-all active:cursor-grabbing ${dragId === o.id ? "opacity-40" : "hover:shadow-md"} ${isHi ? "border-primary ring-2 ring-primary/50" : overdue ? "border-red-300" : "border-border"}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-1.5">
                          {selectMode && (o.crates?.length > 0) && (
                            <input type="checkbox" data-testid={`select-${o.order_code}`} checked={selectedIds.has(o.id)} onChange={() => toggleSelect(o.id)} className="mt-1 h-4 w-4 shrink-0 accent-green-700" />
                          )}
                          <GripVertical className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/40" />
                          <div>
                            <p className="font-semibold">{o.client_name}</p>
                            <p className="font-mono text-xs text-muted-foreground">{o.order_code}</p>
                          </div>
                        </div>
                        <button data-testid={`delete-order-${o.order_code}`} onClick={() => remove(o.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><Boxes className="h-3.5 w-3.5" /> {o.crate_count}</span>
                        <span className="flex items-center gap-1"><Scale className="h-3.5 w-3.5" /> {o.total_weight} kg</span>
                        {o.show_yield && (
                          <span data-testid={`yield-badge-${o.order_code}`} className="flex items-center gap-1 rounded-full bg-amber-glow px-2 py-0.5 font-medium text-amber-deep">
                            <Droplets className="h-3.5 w-3.5" /> {o.estimated_yield} {t("liters")}
                          </span>
                        )}
                        {dur && (
                          <span data-testid={`time-in-stage-${o.order_code}`} className={`flex items-center gap-1 ${heat.overdue ? "font-semibold" : ""}`} style={{ color: heat.color }}>
                            {heat.overdue ? <AlertTriangle className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />} {dur}
                          </span>
                        )}
                      </div>
                      <div className="mt-3 flex gap-1.5">
                        {stage.id !== lastStage && (
                          <Button data-testid={`advance-${o.order_code}`} size="sm" onClick={() => advance(o)} className="flex-1 rounded-lg bg-primary text-xs hover:bg-olive-hover">
                            {t("advance")} <ChevronRight className="ml-0.5 h-3.5 w-3.5" />
                          </Button>
                        )}
                        {o.crates?.length > 0 && (
                          <Button data-testid={`edit-crates-${o.order_code}`} size="icon" variant="outline" onClick={() => setEditOrder(o)} className="h-8 w-8"><Pencil className="h-3.5 w-3.5" /></Button>
                        )}
                        {o.crates?.length > 0 && (
                          <Button data-testid={`tags-${o.order_code}`} size="icon" variant="outline" onClick={() => window.open(`/crate-tags/${o.id}`, "_blank")} className="h-8 w-8"><Tags className="h-3.5 w-3.5" /></Button>
                        )}
                        <Button data-testid={`print-${o.order_code}`} size="icon" variant="outline" onClick={() => navigate(`/print-slip/${o.id}`)} className="h-8 w-8"><Printer className="h-3.5 w-3.5" /></Button>
                        <Button data-testid={`track-${o.order_code}`} size="icon" variant="outline" onClick={() => window.open(`/track/${o.order_code}`, "_blank")} className="h-8 w-8"><QrCode className="h-3.5 w-3.5" /></Button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {editOrder && (
        <EditCratesDialog order={editOrder} t={t} onClose={() => setEditOrder(null)} onDone={() => { setEditOrder(null); load(); }} />
      )}
    </DashboardLayout>
  );
}

function EditCratesDialog({ order, t, onClose, onDone }) {
  const [crates, setCrates] = useState((order.crates || []).map((c) => ({ number: c.number, weight: c.weight })));
  const [busy, setBusy] = useState(false);
  const setNumber = (i, v) => setCrates((c) => c.map((x, idx) => (idx === i ? { ...x, number: v } : x)));
  const setWeight = (i, v) => setCrates((c) => c.map((x, idx) => (idx === i ? { ...x, weight: v } : x)));

  const save = async () => {
    const nums = crates.map((c) => String(c.number).trim());
    if (nums.some((n) => !/^\d+$/.test(n))) { toast.error(t("crate_number_required")); return; }
    const ints = nums.map(Number);
    if (new Set(ints).size !== ints.length) { toast.error(t("crate_number_dup")); return; }
    setBusy(true);
    try {
      await api.put(`/orders/${order.id}/crates`, {
        crates: crates.map((c) => ({ number: parseInt(c.number, 10), weight: parseFloat(c.weight) || 0 })),
      });
      toast.success(t("crates_updated"));
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="edit-crates-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{t("edit_crates")} · {order.order_code}</DialogTitle></DialogHeader>
        <div className="space-y-2">
          {crates.map((c, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`edit-crate-row-${i}`}>
              <Input data-testid={`edit-crate-number-${i}`} type="number" inputMode="numeric" min="1" placeholder={t("crate_number")} value={c.number} onChange={(e) => setNumber(i, e.target.value)} className="w-28 shrink-0" />
              <Input data-testid={`edit-crate-weight-${i}`} type="number" inputMode="decimal" step="0.1" placeholder={t("weight_kg")} value={c.weight} onChange={(e) => setWeight(i, e.target.value)} className="flex-1" />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
          <Button data-testid="save-crates-btn" onClick={save} disabled={busy} className="bg-primary hover:bg-olive-hover">{busy ? t("loading") : t("save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
